import { createRouter, createWebHistory } from "vue-router";
import * as Sentry from "@sentry/vue";
import axios from "axios";
import { redirectToLogin } from "../stores/helpers";
import { useUserStore } from "../stores/user";

function requirePermissions(...permissions) {
    return (to, from) => {
        const userStore = useUserStore();
        const user = userStore.user;

        if (!user.authenticated) {
            redirectToLogin();
            return false;
        }

        const allowed = permissions.some(
            (p) => user.permissions.includes(p) ||
                   user.roles.includes(p),
        );

        if (!allowed) {
            return { name: "dashboard" };
        }
    };
}

const router = createRouter({
    history: createWebHistory(),
    routes: [
        {
            path: "/",
            redirect: { name: `login` },
        },
        {
            path: "/login",
            name: `login`,
            beforeEnter: async (to, from) => {
                const userStore = useUserStore();
                let user = userStore.user;

                if (user.id) {
                    return { name: "dashboard" };
                }

                // No local auth state — but a session cookie may exist
                // (e.g. after a full-page OAuth redirect to app-beta).
                // Try to fetch the user before redirecting to the login page.
                try {
                    await axios.get("/sanctum/csrf-cookie");
                    await userStore.readUser();
                    return { name: "dashboard" };
                } catch {
                    redirectToLogin();
                    return false;
                }
            },
        },
        {
            path: "/redirect/:pathMatch(.*)*",
            name: `redirect`,
            component: () => import("../views/Redirect.vue"),
            meta: {
                title: "Redirecting...",
            },
        },
        {
            path: "/dashboard",
            component: () => import("../routerview.vue"),
            meta: {
                title: "Dashboard",
            },
            children: [
                {
                    name: "dashboard",
                    path: "",
                    component: () => import("../views/Home.vue"),
                },
            ],
        },
        {
            path: "/campaigns",
            component: () => import("../routerview.vue"),
            meta: {
                title: "Campaigns",
            },
            children: [
                {
                    name: "campaigns.view",
                    path: "",
                    component: () =>
                        import("../views/Campaign/CampaignsOverview.vue"),
                },
                {
                    name: "campaigns.new",
                    path: "new",
                    component: () =>
                        import("../views/Campaign/NewCampaign.vue"),
                },
                {
                    name: "campaign.view",
                    path: ":id",
                    component: () => import("../views/Campaign/Campaign.vue"),
                    props: (route) => ({ propCampaignId: route.params.id }),
                },
            ],
            beforeEnter: requirePermissions("edit campaigns", "qa", "admin"),
        },
        {
            path: "/assets",
            component: () => import("../routerview.vue"),
            meta: {
                title: "Assets",
            },
            children: [
                {
                    name: "assets.view",
                    path: "creative/:id",
                    component: () =>
                        import("../views/Asset/CreativeAssets.vue"),
                    props: (route) => ({ creativeId: route.params.id }),
                },
                {
                    name: "assets.campaign",
                    path: "",
                    component: () =>
                        import("../views/Asset/AssetsByCampaign.vue"),
                },
                {
                    name: "assets.creative",
                    path: "campaign/:id",
                    component: () =>
                        import("../views/Asset/CampaignCreatives.vue"),
                    props: (route) => ({ campaignId: route.params.id }),
                },
            ],
            beforeEnter: requirePermissions("edit assets", "qa", "admin"),
        },
        {
            name: "approvals",
            path: "/approvals/:token",
            component: () => import("../views/Approval/Approval.vue"),
            meta: {
                title: "Approvals",
            },
            props: true,
            children: [
                {
                    name: "approvals.id",
                    path: ":approvalId",
                    props: true,
                },
            ],
        },
        {
            path: "/creatives",
            component: () => import("../routerview.vue"),
            meta: {
                title: "Creatives",
            },
            children: [
                {
                    name: "creatives.view",
                    path: "",
                    component: () =>
                        import("../views/Creative/CreativesOverview.vue"),
                },
                {
                    name: "creatives.new",
                    path: "new",
                    component: () =>
                        import("../views/Creative/NewCreative.vue"),
                },
                {
                    name: "creatives.newFromCampaign",
                    path: "new/:campaignId",
                    component: () =>
                        import("../views/Creative/NewCreative.vue"),
                    props: (route) => ({
                        campaignId: parseInt(route.params.campaignId),
                    }),
                },
                {
                    name: "creative.view",
                    path: ":id",
                    component: () => import("../views/Creative/Creative.vue"),
                    props: (route) => ({ propCreativeId: route.params.id }),
                },
            ],
            beforeEnter: requirePermissions("edit creatives", "qa", "admin"),
        },
        {
            path: "/profiles",
            component: () => import("../routerview.vue"),
            meta: {
                title: "Profiles",
            },
            children: [
                {
                    name: "profiles.view",
                    path: "",
                    component: () =>
                        import("../views/Profile/ProfilesOverview.vue"),
                },
                {
                    name: "profiles.new",
                    path: "new",
                    component: () => import("../views/Profile/NewProfile.vue"),
                },
                {
                    name: "profile.view",
                    path: ":id",
                    component: () => import("../views/Profile/Profile.vue"),
                    props: (route) => ({ propProfileId: route.params.id }),
                },
                {
                    name: "profile.errors",
                    path: ":id/errors",
                    component: () => import("../views/Profile/Errors.vue"),
                    props: (route) => ({ propProfileId: route.params.id }),
                },
            ],
            beforeEnter: requirePermissions("edit profiles", "qa", "admin"),
        },
        {
            path: "/conditions",
            component: () => import("../routerview.vue"),
            meta: {
                title: "Conditions",
            },
            children: [
                {
                    name: "conditions.view",
                    path: "",
                    component: () =>
                        import("../views/Condition/ConditionsOverview.vue"),
                },
                {
                    name: "conditions.new",
                    path: "new",
                    component: () =>
                        import("../views/Condition/NewCondition.vue"),
                },
                {
                    name: "condition.view",
                    path: ":id",
                    component: () => import("../views/Condition/Condition.vue"),
                    props: (route) => ({ propConditionId: route.params.id }),
                },
            ],
            beforeEnter: requirePermissions("edit conditions", "qa", "admin"),
        },
        {
            name: "myaccount",
            path: "/myaccount",
            meta: {
                title: "Account",
            },
            component: () => import("../views/MyAccount.vue"),
        },
        { path: "/:pathMatch(.*)*", component: () => import("../views/NotFound.vue") },
    ],
});

router.beforeEach(async (to, from) => {
    const userStore = useUserStore();
    const user = userStore.user;

    const scope = Sentry.getCurrentScope();
    scope.setUser({
        id: user.id,
        username: user.name,
        email: user.email,
    });

    const unauthenticatedRouteNames = [
        `login`,
        `approvals`,
        `approvals.id`,
    ];

    window.restrictedRoute = !unauthenticatedRouteNames.includes(to.name);

    if (window.restrictedRoute && !user.authenticated) {
        try {
            // Ensure CSRF cookie is set before attempting to read user.
            // This is needed when arriving from an OAuth redirect where the
            // session cookie exists but the XSRF-TOKEN cookie hasn't been set yet
            // (e.g. app-beta.fluxoutdoor.com after full-page OAuth flow).
            await axios.get("/sanctum/csrf-cookie");
            await userStore.readUser();
        } catch (error) {
            console.error("Error fetching user data:", error);
            localStorage.removeItem('user');
            redirectToLogin();
            return false;
        }
    }

    const nearestWithTitle = to.matched
        .slice()
        .reverse()
        .find((r) => r.meta && r.meta.title);

    if (nearestWithTitle) document.title = nearestWithTitle.meta.title;
});

export default router;
